// src/components/themes/default/admin/BranchOrdersTab.tsx
"use client";

import { useState, useEffect, useCallback } from "react";
import Swal from "sweetalert2";
import {
  ShoppingBag,
  Search,
  Eye,
  CheckCircle2,
  Clock,
  Truck,
  XCircle,
  PackageCheck,
  RefreshCw,
  Loader2,
  X,
} from "lucide-react";

interface BranchOrdersTabProps {
  subdomain: string;
  branchId: number;
  branchName: string;
  primaryColor?: string;
  theme?: "dark" | "light";
}

export default function BranchOrdersTab({
  subdomain,
  branchId,
  branchName,
  primaryColor = "#059669",
  theme = "dark",
}: BranchOrdersTabProps) {
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [viewOrder, setViewOrder] = useState<any | null>(null);
  const [statusLoadingId, setStatusLoadingId] = useState<string | null>(null);

  const getApiUrl = (endpoint: string) => {
    const isLocal =
      typeof window !== "undefined" &&
      (window.location.hostname.includes("localhost") ||
        window.location.hostname === "127.0.0.1");
    return isLocal ? `http://${subdomain}.localhost:8000${endpoint}` : endpoint;
  };

  const getAuthToken = () => {
    if (typeof window === "undefined") return "";
    const pathParts = window.location.pathname.split("/b/");
    const currentBranchSlug = pathParts[1]?.split("/")[0]?.split("?")[0] || "";

    if (currentBranchSlug) {
      const branchAuth = localStorage.getItem(`branch_auth_${subdomain}_${currentBranchSlug}`);
      if (branchAuth) {
        try {
          const parsed = JSON.parse(branchAuth);
          if (parsed.token) return parsed.token;
        } catch {}
      }
    }
    return localStorage.getItem("merchant_token") || localStorage.getItem("token") || "";
  };

  // ১. শুধুমাত্র এই ব্রাঞ্চের অর্ডার ফেচ করা
  const fetchBranchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const token = getAuthToken();
      const res = await fetch(getApiUrl(`/api/v1/orders/?branch_id=${branchId}&admin=true`), {
        headers: {
          Accept: "application/json",
          "X-Branch-ID": String(branchId),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (res.ok) {
        const data = await res.json();
        setOrders(Array.isArray(data) ? data : data.results || []);
      }
    } catch (err) {
      console.error("Error fetching branch orders:", err);
    } finally {
      setLoading(false);
    }
  }, [subdomain, branchId]);

  useEffect(() => {
    fetchBranchOrders();
  }, [fetchBranchOrders]);

  // ২. অর্ডার স্ট্যাটাস পরিবর্তন
  const handleUpdateStatus = async (orderNumber: string, newStatus: string) => {
    setStatusLoadingId(orderNumber);

    // পূর্বের স্টেট ব্যাকআপ রাখা
    const previousOrders = [...orders];

    // অপটিমিস্টিক UI আপডেট (তাত্ক্ষণিক UI পরিবর্তন)
    setOrders((prev) =>
      prev.map((o) =>
        o.order_number === orderNumber
          ? {
              ...o,
              status: newStatus,
              payment_status: newStatus === "delivered" ? "paid" : o.payment_status,
            }
          : o
      )
    );

    if (viewOrder && viewOrder.order_number === orderNumber) {
      setViewOrder((prev: any) => ({
        ...prev,
        status: newStatus,
        payment_status: newStatus === "delivered" ? "paid" : prev.payment_status,
      }));
    }

    try {
      const token = getAuthToken();
      const res = await fetch(getApiUrl(`/api/v1/orders/${orderNumber}/update-status/`), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Branch-ID": String(branchId),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ status: newStatus, branch_id: branchId }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "স্ট্যাটাস পরিবর্তন করা সম্ভব হয়নি।");
      }

      Swal.fire({
        position: "top",
        icon: "success",
        title: "স্ট্যাটাস আপডেট হয়েছে!",
        text: `অর্ডার #${orderNumber} এখন ${newStatus.toUpperCase()}`,
        showConfirmButton: false,
        timer: 1500,
        background: "#090d16",
        color: "#ffffff",
        customClass: { container: "!z-[999999]" },
      });

      // ব্যাকএন্ডের সাথে ফুল সিঙ্ক
      fetchBranchOrders();
    } catch (err: any) {
      // ব্যর্থ হলে আগের স্টেটে ফিরিয়ে আনা
      setOrders(previousOrders);
      Swal.fire({
        position: "top",
        icon: "error",
        title: "ব্যর্থ হয়েছে",
        text: err.message || "সার্ভারে যোগাযোগ করা যায়নি।",
        background: "#090d16",
        color: "#ffffff",
        customClass: { container: "!z-[999999]" },
      });
    } finally {
      setStatusLoadingId(null);
    }
  };

  // স্ট্যাটাস ব্যাজ স্টাইল
  const getStatusBadge = (status: string) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return { label: "Pending", icon: Clock, style: "bg-amber-500/10 text-amber-400 border-amber-500/20" };
      case "processing":
        return { label: "Processing", icon: PackageCheck, style: "bg-blue-500/10 text-blue-400 border-blue-500/20" };
      case "shipped":
        return { label: "Shipped", icon: Truck, style: "bg-purple-500/10 text-purple-400 border-purple-500/20" };
      case "delivered":
        return { label: "Delivered", icon: CheckCircle2, style: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" };
      case "cancelled":
        return { label: "Cancelled", icon: XCircle, style: "bg-rose-500/10 text-rose-400 border-rose-500/20" };
      default:
        return { label: status, icon: Clock, style: "bg-slate-800 text-slate-400 border-slate-700" };
    }
  };

  const filteredOrders = orders.filter((o) => {
    const q = search.toLowerCase();
    const matchSearch =
      o.order_number?.toLowerCase().includes(q) ||
      o.customer_name?.toLowerCase().includes(q) ||
      o.customer_phone?.includes(q);

    const matchStatus = statusFilter === "all" || o.status?.toLowerCase() === statusFilter.toLowerCase();
    return matchSearch && matchStatus;
  });

  const totalRevenue = orders.reduce((sum, o) => sum + (Number(o.total) || 0), 0);
  const pendingCount = orders.filter((o) => o.status === "pending").length;
  const deliveredCount = orders.filter((o) => o.status === "delivered").length;

  return (
    <div className="space-y-5 animate-fadeIn">
      {/* ১. টপ ব্যানার ও মেট্রিক্স */}
      <div className="p-5 sm:p-6 rounded-3xl bg-slate-900 border border-slate-800 text-white flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-xl">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
              <ShoppingBag className="w-5 h-5" />
            </div>
            <h1 className="text-lg sm:text-xl font-black">
              {branchName} Orders Dashboard
            </h1>
          </div>
          <p className="text-xs text-slate-400 mt-1">
            শুধুমাত্র এই আউটলেটের বিক্রয় ও লাইভ অর্ডার ট্র্যাকিং ({orders.length} টি মোট অর্ডার)
          </p>
        </div>

        <button
          onClick={fetchBranchOrders}
          className="flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-slate-950 border border-slate-800 hover:bg-slate-800 text-slate-300 hover:text-white transition-colors cursor-pointer w-fit"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin text-emerald-400" : ""}`} />
          <span className="text-xs font-bold">Refresh Orders</span>
        </button>
      </div>

      {/* ২. কুইক স্ট্যাটাস কাউন্টার কার্ডস */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3.5">
        <div className="p-4 rounded-2xl bg-slate-900 border border-slate-800">
          <span className="text-[10px] uppercase font-bold text-slate-400 block">Total Revenue</span>
          <span className="text-lg font-black font-mono text-emerald-400 mt-1 block">
            ৳{totalRevenue.toLocaleString()}
          </span>
        </div>
        <div className="p-4 rounded-2xl bg-slate-900 border border-slate-800">
          <span className="text-[10px] uppercase font-bold text-slate-400 block">Pending Orders</span>
          <span className="text-lg font-black font-mono text-amber-400 mt-1 block">
            {pendingCount} Orders
          </span>
        </div>
        <div className="p-4 rounded-2xl bg-slate-900 border border-slate-800">
          <span className="text-[10px] uppercase font-bold text-slate-400 block">Delivered Orders</span>
          <span className="text-lg font-black font-mono text-cyan-400 mt-1 block">
            {deliveredCount} Completed
          </span>
        </div>
        <div className="p-4 rounded-2xl bg-slate-900 border border-slate-800">
          <span className="text-[10px] uppercase font-bold text-slate-400 block">Total Orders</span>
          <span className="text-lg font-black font-mono text-white mt-1 block">
            {orders.length} Placed
          </span>
        </div>
      </div>

      {/* ৩. ফিল্টার ও সার্চ বার */}
      <div className="p-3 sm:p-4 rounded-2xl bg-slate-900 border border-slate-800 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by Order ID, Customer name or Phone..."
            className="w-full pl-10 pr-4 py-2 rounded-xl text-xs font-bold bg-slate-950 border border-slate-800 text-white outline-none focus:border-emerald-500"
          />
        </div>

        <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-none pb-1 sm:pb-0 text-xs font-bold">
          {["all", "pending", "processing", "shipped", "delivered", "cancelled"].map((st) => (
            <button
              key={st}
              onClick={() => setStatusFilter(st)}
              className={`px-3 py-1.5 rounded-xl capitalize transition-all cursor-pointer whitespace-nowrap border ${
                statusFilter === st
                  ? "bg-emerald-600 text-white border-emerald-600 shadow-sm"
                  : "bg-slate-950 text-slate-400 border-slate-800 hover:text-white"
              }`}
            >
              {st}
            </button>
          ))}
        </div>
      </div>

      {/* ৪. অর্ডার টেবিল */}
      <div className="rounded-3xl overflow-hidden border border-slate-800 bg-slate-900 shadow-xl">
        <div className="overflow-x-auto w-full">
          <table className="w-full text-left text-xs min-w-[750px]">
            <thead className="uppercase text-[10px] font-black border-b border-slate-800 bg-slate-950/70 text-slate-400 tracking-wider">
              <tr>
                <th className="py-4 px-5">Order ID</th>
                <th className="py-4 px-4">Customer Info</th>
                <th className="py-4 px-4">Bill Amount</th>
                <th className="py-4 px-4">Payment</th>
                <th className="py-4 px-4">Status & Action</th>
                <th className="py-4 px-5 text-right">View</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800 text-slate-300 font-medium">
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center py-16 text-slate-400">
                    <Loader2 className="w-8 h-8 mx-auto animate-spin text-emerald-400 mb-2" />
                    <p>ব্রাঞ্চ অর্ডার তালিকা লোড হচ্ছে...</p>
                  </td>
                </tr>
              ) : filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-16 text-slate-500 space-y-2">
                    <ShoppingBag className="w-10 h-10 mx-auto opacity-30 text-slate-500 mb-2" />
                    <p>কোনো অর্ডার পাওয়া যায়নি।</p>
                  </td>
                </tr>
              ) : (
                filteredOrders.map((ord) => {
                  const badge = getStatusBadge(ord.status);
                  const Icon = badge.icon;

                  return (
                    <tr key={ord.id} className="hover:bg-slate-800/40 transition-colors">
                      <td className="py-4 px-5">
                        <span className="font-mono font-bold text-white block">#{ord.order_number}</span>
                        <span className="text-[10px] text-slate-400 font-mono">
                          {new Date(ord.created_at).toLocaleDateString("en-GB")}
                        </span>
                      </td>

                      <td className="py-4 px-4">
                        <span className="font-bold text-white block">{ord.customer_name}</span>
                        <span className="text-[10px] text-slate-400 font-mono">{ord.customer_phone}</span>
                      </td>

                      <td className="py-4 px-4">
                        <span className="font-mono font-black text-emerald-400 text-sm">
                          ৳{Number(ord.total).toLocaleString()}
                        </span>
                        <span className="text-[10px] text-slate-400 block">{ord.items?.length || 1} Items</span>
                      </td>

                      <td className="py-4 px-4">
                        <span className="uppercase text-[10px] font-bold px-2 py-0.5 rounded-md bg-slate-950 border border-slate-800 text-slate-300 inline-block">
                          {ord.payment_method || "COD"}
                        </span>
                        <span className={`block text-[10px] mt-0.5 ${ord.payment_status === "paid" ? "text-emerald-400 font-bold" : "text-amber-400"}`}>
                          {ord.payment_status?.toUpperCase() || "PENDING"}
                        </span>
                      </td>

                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2">
                          <span className={`px-2.5 py-1 rounded-xl text-[10px] font-black uppercase border flex items-center gap-1.5 ${badge.style}`}>
                            <Icon className="w-3 h-3" />
                            <span>{badge.label}</span>
                          </span>

                          {/* Quick Status Dropdown */}
                          <div className="relative">
                            <select
                              value={ord.status}
                              disabled={statusLoadingId === ord.order_number}
                              onChange={(e) => handleUpdateStatus(ord.order_number, e.target.value)}
                              className="bg-slate-950 border border-slate-800 rounded-xl px-2.5 py-1 text-[11px] font-bold text-slate-300 outline-none cursor-pointer focus:border-emerald-500 disabled:opacity-50"
                            >
                              <option value="pending">Pending</option>
                              <option value="processing">Processing</option>
                              <option value="shipped">Shipped</option>
                              <option value="delivered">Delivered</option>
                              <option value="cancelled">Cancelled</option>
                            </select>
                            {statusLoadingId === ord.order_number && (
                              <Loader2 className="w-3 h-3 animate-spin text-emerald-400 absolute right-1.5 top-2 pointer-events-none" />
                            )}
                          </div>
                        </div>
                      </td>

                      <td className="py-4 px-5 text-right whitespace-nowrap">
                        <button
                          onClick={() => setViewOrder(ord)}
                          className="p-2 rounded-xl bg-slate-950 border border-slate-800 text-slate-400 hover:text-white hover:bg-slate-800 transition-colors cursor-pointer"
                          title="অর্ডারের বিবরণ দেখুন"
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ৫. অর্ডার ডিটেইলস মোডাল */}
      {viewOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/80 backdrop-blur-md animate-fadeIn">
          <div className="w-full max-w-lg rounded-3xl bg-[#090d16] border border-slate-800 text-white shadow-2xl p-6 space-y-4 max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <div>
                <h3 className="text-sm font-black">Order Details</h3>
                <span className="text-xs font-mono text-emerald-400">#{viewOrder.order_number}</span>
              </div>
              <button
                onClick={() => setViewOrder(null)}
                className="p-1.5 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Customer Info */}
            <div className="p-3.5 rounded-2xl bg-slate-950 border border-slate-800 space-y-1 text-xs">
              <span className="text-[10px] uppercase font-bold text-slate-400 block">Customer Information</span>
              <p className="font-black text-white">{viewOrder.customer_name}</p>
              <p className="font-mono text-slate-300">{viewOrder.customer_phone}</p>
              <p className="text-slate-400 leading-relaxed">{viewOrder.full_address || viewOrder.shipping_address?.address}</p>
              {viewOrder.notes && (
                <p className="p-2 rounded-xl bg-slate-900 border border-slate-800 text-amber-400 text-[11px] mt-2">
                  Note: {viewOrder.notes}
                </p>
              )}
            </div>

            {/* Order Items */}
            <div className="space-y-2">
              <span className="text-[10px] uppercase font-bold text-slate-400 block">Order Items</span>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {viewOrder.items?.map((it: any) => (
                  <div
                    key={it.id}
                    className="p-2.5 rounded-xl bg-slate-950 border border-slate-800 flex items-center justify-between text-xs"
                  >
                    <div>
                      <p className="font-bold text-white">{it.product_name}</p>
                      <span className="text-[10px] text-slate-500 font-mono">Qty: {it.quantity}</span>
                    </div>
                    <span className="font-mono font-bold text-emerald-400">
                      ৳{Number(it.total_price).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Bill Summary */}
            <div className="p-3 rounded-2xl bg-slate-950 border border-slate-800 flex items-center justify-between text-xs font-bold">
              <span>Total Bill:</span>
              <span className="text-base font-black font-mono text-emerald-400">
                ৳{Number(viewOrder.total).toLocaleString()}
              </span>
            </div>

            <button
              onClick={() => setViewOrder(null)}
              className="w-full py-2.5 rounded-2xl bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold cursor-pointer"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}